import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FetchNextPageOptions, type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import type { EpisodesWithPagination } from '@jwp/ott-common/types/series';
import type { Pagination } from '@jwp/ott-common/types/pagination';
import ContentApiService from '@jwp/ott-common/src/services/ContentApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { generatePlaylistPlaceholder } from '@jwp/ott-common/src/utils/collection';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';

const PLACEHOLDER_ITEM_COUNT = 10;
const placeholderData = generatePlaylistPlaceholder(PLACEHOLDER_ITEM_COUNT);

const getNextPageParam = (pagination: Pagination) => {
  const { page, page_limit, total } = pagination;

  // In case there are no more episodes in a season to fetch
  if (page_limit * page >= total) {
    return undefined;
  }

  return page;
};

export const useEpisodes = (
  seriesId: string | undefined,
  seasonNumber: string | undefined,
  options: { enabled: boolean; usePlaceholderData?: boolean },
): {
  data: EpisodesWithPagination[];
  allEpisodes: PlaylistItem[];
  hasNextPage: boolean;
  fetchNextPage: (options?: FetchNextPageOptions) => void;
  isLoading: boolean;
  isFetching: boolean;
} => {
  const contentApiService = getModule(ContentApiService);
  const config = useConfigStore.getState().config;

  // Determine currently selected language
  const { i18n } = useTranslation();
  const language = i18n.language;

  const {
    data,
    fetchNextPage,
    isLoading,
    isFetching,
    hasNextPage = false,
  } = useInfiniteQuery<EpisodesWithPagination, Error, InfiniteData<EpisodesWithPagination>, [string | undefined, string | undefined, string], number>({
    queryKey: [seriesId, seasonNumber, language],
    queryFn: async ({ pageParam }) => {
      // Get episodes from a selected series using pagination
      return contentApiService.getEpisodes({
        seriesId,
        seasonNumber: typeof seasonNumber === 'string' && seasonNumber ? Number(seasonNumber) : undefined,
        pageOffset: pageParam,
        language,
        config,
      });
    },
    getNextPageParam: (lastPage) => getNextPageParam(lastPage?.pagination),
    placeholderData: options.usePlaceholderData
      ? {
          pages: [
            {
              episodes: placeholderData.playlist,
              pagination: { page: 0, page_limit: PLACEHOLDER_ITEM_COUNT, total: PLACEHOLDER_ITEM_COUNT },
            },
          ],
          pageParams: [0],
        }
      : undefined,
    initialPageParam: 0,
    enabled: options.enabled,
  });

  const allEpisodes = useMemo(() => {
    return data?.pages.flatMap((page) => page.episodes) || [];
  }, [data]);

  return {
    data: data?.pages || [],
    allEpisodes,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
  };
};
